/**
 * Single source of truth for subscription plans across Petso, Petso Pro, and Farmso.
 * Plans and pricing are NOT shared between platforms: each product has its own plans.
 *
 * - Petso app: use getProductConfig('petso') only — shows Petso tab plans/pricing.
 * - Petso Pro app: use getProductConfig('petso-pro') only — shows Petso Pro tab plans/pricing.
 * - Farmso app: use getProductConfig('farmso') only — shows Farmso tab plans/pricing.
 * - Admin: manages all three in separate tabs; edits in a tab persist and reflect in that platform only.
 *   Persistence: localStorage (same origin) so Admin edits show in platform apps when served from same origin.
 */

export interface Plan {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  monthlyPrice: number;
  annualPrice: number;
  freemiumMonths?: number;
  features: string[];
  limits: {
    pets?: number;
    storage?: number;
    appointments?: number;
    animals?: number;
    teamMembers?: number;
  };
}

export interface ProductConfig {
  id: string;
  name: string;
  billingEnabled: boolean;
  plans: Plan[];
}

export const INITIAL_SUBSCRIPTION_PRODUCTS: ProductConfig[] = [
  {
    id: 'petso',
    name: 'Petso',
    billingEnabled: false,
    plans: [
      {
        id: 'free',
        name: 'Free',
        status: 'active',
        monthlyPrice: 0,
        annualPrice: 0,
        features: [
          'Up to 2 pets',
          'Basic health records',
          'Appointment booking',
          'Community support',
          'Basic social features',
        ],
        limits: {
          pets: 2,
          storage: 1024,
        },
      },
      {
        id: 'freemium',
        name: 'Freemium',
        status: 'active',
        monthlyPrice: 9.99,
        annualPrice: 95.9,
        freemiumMonths: 3,
        features: [
          'Unlimited pets',
          'Advanced health tracking',
          'Priority support',
          'AI health insights',
          'Premium social features',
          'Family sharing',
        ],
        limits: {
          pets: 999,
          storage: 10240,
        },
      },
      {
        id: 'paid',
        name: 'Paid',
        status: 'active',
        monthlyPrice: 9.99,
        annualPrice: 95.9,
        features: [
          'Unlimited pets',
          'Advanced health tracking',
          'Priority support',
          'AI health insights',
          'Premium social features',
          'Family sharing',
        ],
        limits: {
          pets: 999,
          storage: 10240,
        },
      },
    ],
  },
  {
    id: 'petso-pro',
    name: 'Petso Pro',
    billingEnabled: false,
    plans: [
      {
        id: 'free',
        name: 'Free',
        status: 'active',
        monthlyPrice: 0,
        annualPrice: 0,
        features: [
          'Basic business profile',
          'Up to 10 clients',
          'Basic appointment system',
          'Community support',
          'Basic invoicing',
        ],
        limits: {
          appointments: 25,
          storage: 2048,
        },
      },
      {
        id: 'starter',
        name: 'Starter Freemium',
        status: 'active',
        monthlyPrice: 79.99,
        annualPrice: 767.9,
        freemiumMonths: 3,
        features: [
          'Professional profile',
          'Up to 100 clients',
          'Full appointment system',
          'Email support',
          'Complete invoicing',
          'Basic analytics',
        ],
        limits: {
          appointments: 100,
          storage: 5120,
        },
      },
      {
        id: 'professional',
        name: 'Professional',
        status: 'active',
        monthlyPrice: 79.99,
        annualPrice: 767.9,
        features: [
          'Advanced profile',
          'Unlimited clients',
          'Team management',
          'Priority support',
          'Advanced analytics',
          'AI insights',
        ],
        limits: {
          appointments: 999,
          storage: 20480,
          teamMembers: 5,
        },
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        status: 'active',
        monthlyPrice: 199.99,
        annualPrice: 1919.9,
        features: [
          'Multi-location support',
          'Unlimited everything',
          'Dedicated account manager',
          'Custom integrations',
          'Advanced AI features',
          'White-label options',
        ],
        limits: {
          appointments: 9999,
          storage: 102400,
          teamMembers: 999,
        },
      },
    ],
  },
  {
    id: 'farmso',
    name: 'Farmso',
    billingEnabled: false,
    plans: [
      {
        id: 'free',
        name: 'Free',
        status: 'active',
        monthlyPrice: 0,
        annualPrice: 0,
        features: [
          'Up to 20 animals',
          'Basic farm management',
          'Simple compliance tracking',
          'Community support',
          'Basic sales tracking',
        ],
        limits: {
          animals: 50,
          storage: 2048,
        },
      },
      {
        id: 'starter',
        name: 'Starter Freemium',
        status: 'active',
        monthlyPrice: 99.99,
        annualPrice: 959.9,
        freemiumMonths: 3,
        features: [
          'Up to 200 animals',
          'Complete farm management',
          'Full compliance tracking',
          'Email support',
          'Advanced sales & invoicing',
          'Basic analytics',
        ],
        limits: {
          animals: 200,
          storage: 10240,
        },
      },
      {
        id: 'professional',
        name: 'Professional',
        status: 'active',
        monthlyPrice: 99.99,
        annualPrice: 959.9,
        features: [
          'Unlimited animals',
          'Multi-farm support',
          'Team collaboration',
          'Priority support',
          'Advanced analytics',
          'AI farm insights',
        ],
        limits: {
          animals: 1000,
          storage: 51200,
          teamMembers: 5,
        },
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        status: 'active',
        monthlyPrice: 299.99,
        annualPrice: 2879.9,
        features: [
          'Enterprise-scale farming',
          'Supply chain management',
          'Dedicated support',
          'Custom integrations',
          'Advanced AI features',
          'Government portal sync',
        ],
        limits: {
          animals: 99999,
          storage: 512000,
          teamMembers: 999,
        },
      },
    ],
  },
];

const STORAGE_KEY = 'petso_subscription_products';

/** Load persisted subscription config (from Admin) for same-origin sync. */
export function loadSubscriptionProductsFromStorage(): ProductConfig[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProductConfig[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

/** Persist subscription config so platform apps can show Admin updates (same origin). */
export function saveSubscriptionProductsToStorage(
  products: ProductConfig[]
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
  } catch {
    // ignore
  }
}
